import React, { useState, useEffect } from 'react';
import { AssetWorkingAvailability, AssetDayAvailability, getAssetAvailability, updateAssetAvailability, createAssetAvailability } from '../../lib/assetAvailability';
import { Pencil, MapPin } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import ServiceAreasModal from '../resources/ServiceAreasModal';

interface AssetAvailabilityProps {
  assetId: string;
  businessId: string;
}

const DEFAULT_AVAILABILITY: Partial<AssetWorkingAvailability> = {
  monday: { enabled: false, shifts: [] },
  tuesday: { enabled: false, shifts: [] },
  wednesday: { enabled: false, shifts: [] },
  thursday: { enabled: false, shifts: [] },
  friday: { enabled: false, shifts: [] },
  saturday: { enabled: false, shifts: [] },
  sunday: { enabled: false, shifts: [] }
};

const DAYS = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' }
];

const AssetAvailabilityComponent: React.FC<AssetAvailabilityProps> = ({ assetId, businessId }) => {
  const [availability, setAvailability] = useState<AssetWorkingAvailability | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState<AssetWorkingAvailability | null>(null);
  const [mobileAvailable, setMobileAvailable] = useState(false);
  const [mobileLoading, setMobileLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [isAreasModalOpen, setIsAreasModalOpen] = useState(false);
  const [currentAreas, setCurrentAreas] = useState<string[]>([]);

  useEffect(() => {
    setMobileAvailable(false);
    setMobileLoading(true);
    setAvailability(null);
    setLoading(true);
    setError(null);
    setEditMode(false);
    setFormData(null);
    setCurrentAreas([]);

    fetchAvailability();
    fetchMobileAvailability();
  }, [assetId]);

  const fetchMobileAvailability = async () => {
    const currentAssetId = assetId;
    if (!supabase) {
      setMobileAvailable(false);
      setMobileLoading(false);
      return;
    }

    try {
      setMobileLoading(true);
      const { data, error } = await supabase
        .from('mobile_asset_availability')
        .select('id, mobile_available')
        .eq('asset_id', currentAssetId)
        .maybeSingle();

      if (currentAssetId !== assetId) return;

      if (error && error.code !== 'PGRST116') throw error;

      setMobileAvailable(!!data);
    } catch (err) {
      if (currentAssetId !== assetId) return;
      console.error('Error fetching mobile asset availability:', err);
      setMobileAvailable(false);
    } finally {
      if (currentAssetId === assetId) setMobileLoading(false);
    }
  };

  const handleMobileToggle = async () => {
    if (!supabase || !availability) return;
    const currentAssetId = assetId;
    const newValue = !mobileAvailable;

    try {
      setMobileLoading(true);

      if (newValue) {
        const working_hours: Record<string, any> = {};
        const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
        days.forEach(day => {
          working_hours[day] = {
            enabled: availability[day].enabled,
            radius: 0,
            shifts: availability[day].shifts,
            areas: []
          };
        });

        await supabase
          .from('mobile_asset_availability')
          .delete()
          .eq('asset_id', currentAssetId);

        if (currentAssetId !== assetId) return;

        const { error: insertError } = await supabase
          .from('mobile_asset_availability')
          .insert({
            asset_id: currentAssetId,
            business_id: businessId,
            mobile_available: true,
            working_hours,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (currentAssetId !== assetId) return;
        if (insertError) throw insertError;
      } else {
        const { error: deleteError } = await supabase
          .from('mobile_asset_availability')
          .delete()
          .eq('asset_id', currentAssetId);

        if (currentAssetId !== assetId) return;
        if (deleteError) throw deleteError;
      }

      if (currentAssetId !== assetId) return;
      setMobileAvailable(newValue);
      await fetchMobileAvailability();
      if (newValue) await fetchAvailability();
    } catch (err) {
      if (currentAssetId !== assetId) return;
      console.error('Error toggling mobile asset availability:', err);
      setMobileAvailable(!newValue);
      await fetchMobileAvailability();
    } finally {
      if (currentAssetId === assetId) setMobileLoading(false);
    }
  };

  const fetchAvailability = async () => {
    const currentAssetId = assetId;
    try {
      setLoading(true);
      setError(null);
      setAvailability(null);
      setFormData(null);

      let data = await getAssetAvailability(currentAssetId);
      if (currentAssetId !== assetId) return;

      if (!data) {
        await createAssetAvailability(currentAssetId, businessId, DEFAULT_AVAILABILITY);
        data = await getAssetAvailability(currentAssetId);
        if (currentAssetId !== assetId) return;
        if (!data) throw new Error('Failed to initialize availability data');
      }

      if (supabase) {
        try {
          const { data: mobileData, error: mobileError } = await supabase
            .from('mobile_asset_availability')
            .select('working_hours')
            .eq('asset_id', currentAssetId)
            .maybeSingle();

          if (currentAssetId !== assetId) return;

          if (!mobileError && mobileData?.working_hours) {
            const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
            days.forEach(day => {
              if (data![day] && mobileData.working_hours[day]?.areas) {
                (data![day] as any).areas = mobileData.working_hours[day].areas;
              }
            });
          }
        } catch {
          // Non-critical — continue without areas
        }
      }

      if (currentAssetId === assetId) {
        setAvailability(data);
        setFormData(data);
      }
    } catch (err) {
      if (currentAssetId === assetId) {
        setError(err instanceof Error ? err.message : 'Failed to load availability data');
      }
    } finally {
      if (currentAssetId === assetId) setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData) return;

    try {
      setLoading(true);
      await updateAssetAvailability(assetId, formData);

      if (mobileAvailable && supabase) {
        const { data: existingRecord } = await supabase
          .from('mobile_asset_availability')
          .select('id, working_hours')
          .eq('asset_id', assetId)
          .maybeSingle();

        const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
        const working_hours: Record<string, any> = {};
        days.forEach(day => {
          working_hours[day] = {
            enabled: formData[day].enabled,
            shifts: formData[day].shifts,
            radius: 0,
            areas: existingRecord?.working_hours?.[day]?.areas || []
          };
        });

        if (existingRecord) {
          const { error: updateError } = await supabase
            .from('mobile_asset_availability')
            .update({
              working_hours,
              mobile_available: true,
              updated_at: new Date().toISOString()
            })
            .eq('asset_id', assetId);
          if (updateError) throw updateError;
        } else {
          const { error: insertError } = await supabase
            .from('mobile_asset_availability')
            .insert({
              asset_id: assetId,
              business_id: businessId,
              mobile_available: true,
              working_hours,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            });
          if (insertError) throw insertError;
        }
      }

      setAvailability(formData);
      setEditMode(false);
      await fetchMobileAvailability();
    } catch (err) {
      console.error('Error saving asset availability:', err);
      setError(err instanceof Error ? err.message : 'Failed to save availability data');
      await fetchMobileAvailability();
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDay = (dayKey: keyof AssetWorkingAvailability) => {
    if (!formData) return;
    const day = formData[dayKey] as AssetDayAvailability;
    setFormData({
      ...formData,
      [dayKey]: {
        enabled: !day.enabled,
        shifts: day.enabled ? [] : [{ start: '09:00', end: '17:00' }]
      }
    });
  };

  const handleShiftChange = (dayKey: keyof AssetWorkingAvailability, start: string, end: string) => {
    if (!formData) return;
    setFormData({
      ...formData,
      [dayKey]: { enabled: true, shifts: [{ start, end }] }
    });
  };

  const fetchCurrentAreas = async (day: string) => {
    if (!supabase) return;
    try {
      const { data, error } = await supabase
        .from('mobile_asset_availability')
        .select('working_hours')
        .eq('asset_id', assetId)
        .single();
      if (error) return;
      setCurrentAreas(data?.working_hours?.[day.toLowerCase()]?.areas || []);
    } catch {
      // Non-critical
    }
  };

  const handleAreasClick = async (day: string) => {
    setSelectedDay(day);
    await fetchCurrentAreas(day);
    setIsAreasModalOpen(true);
  };

  const handleSaveAreas = async (selectedAreas: string[]) => {
    if (!supabase || !selectedDay) return;
    try {
      setMobileLoading(true);

      const { data: currentData } = await supabase
        .from('mobile_asset_availability')
        .select('working_hours, postal_codes')
        .eq('asset_id', assetId)
        .single();

      const { data: serviceAreas, error: serviceAreasError } = await supabase
        .from('service_areas')
        .select('*')
        .in('id', selectedAreas);

      if (serviceAreasError) throw serviceAreasError;

      const allPostalCodes = serviceAreas?.reduce((acc: string[], area) => {
        return [...acc, ...(area.postal_codes || [])];
      }, []) || [];

      const working_hours = currentData?.working_hours || {
        monday: { enabled: false, shifts: [], areas: [] },
        tuesday: { enabled: false, shifts: [], areas: [] },
        wednesday: { enabled: false, shifts: [], areas: [] },
        thursday: { enabled: false, shifts: [], areas: [] },
        friday: { enabled: false, shifts: [], areas: [] },
        saturday: { enabled: false, shifts: [], areas: [] },
        sunday: { enabled: false, shifts: [], areas: [] }
      };

      const dayKey = selectedDay.toLowerCase();
      working_hours[dayKey] = {
        ...working_hours[dayKey],
        areas: selectedAreas,
        enabled: true,
        shifts: working_hours[dayKey]?.shifts || [{ start: '09:00', end: '17:00' }]
      };

      const postal_codes = {
        ...(currentData?.postal_codes || {}),
        [dayKey]: allPostalCodes
      };

      await supabase
        .from('mobile_asset_availability')
        .delete()
        .eq('asset_id', assetId);

      const { error: insertError } = await supabase
        .from('mobile_asset_availability')
        .insert({
          asset_id: assetId,
          business_id: businessId,
          mobile_available: true,
          working_hours,
          postal_codes,
          updated_at: new Date().toISOString()
        });

      if (insertError) throw insertError;

      if (availability) {
        const dk = selectedDay.toLowerCase() as keyof AssetWorkingAvailability;
        const currentDay = availability[dk] as AssetDayAvailability;
        setAvailability({
          ...availability,
          [dk]: {
            enabled: currentDay.enabled,
            shifts: currentDay.shifts,
            areas: selectedAreas
          }
        });
      }

      setCurrentAreas(selectedAreas);
      setIsAreasModalOpen(false);
    } catch (err) {
      console.error('Error updating service areas for asset:', err);
    } finally {
      setMobileLoading(false);
    }
  };

  if (loading) {
    return <div className="p-4 text-center">Loading availability...</div>;
  }

  if (error) {
    return <div className="p-4 text-red-500">{error}</div>;
  }

  if (!availability) {
    return <div className="p-4 text-center">No availability data found</div>;
  }

  return (
    <div className="space-y-0">
      {editMode && (
        <div className="px-2 py-2 sm:px-6 sm:py-3 border-b border-purple-200/30 bg-purple-50/30 flex justify-end items-center space-x-2">
          <button
            onClick={() => {
              setFormData(availability);
              setEditMode(false);
            }}
            className="inline-flex items-center justify-center px-3 py-2 text-xs font-semibold text-gray-700 bg-gray-100/80 backdrop-blur-xl rounded-xl hover:bg-gray-200/80 transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation"
            style={{
              minHeight: '32px',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {loading ? (
              <>
                <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                Saving...
              </>
            ) : (
              'Save'
            )}
          </button>
        </div>
      )}

      {/* Mobile / Online Booking Section */}
      <div className="px-2 py-1.5 sm:px-6 sm:py-4 border-b border-purple-200/30 bg-purple-50/30 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <MapPin className="h-4 w-4 text-gray-500" />
          <span className="text-xs sm:text-sm font-semibold text-gray-700">Mobile Service</span>
        </div>
        <div className="flex items-center space-x-2 sm:space-x-3">
          <button
            onClick={handleMobileToggle}
            disabled={mobileLoading}
            className={`
              relative inline-flex h-5 w-9 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
              transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:ring-offset-2
              ${mobileAvailable ? 'bg-blue-600' : 'bg-gray-300'}
              ${mobileLoading ? 'opacity-50 cursor-not-allowed' : ''}
            `}
            role="switch"
            aria-checked={mobileAvailable}
          >
            <span className="sr-only">Enable mobile service</span>
            <span
              className={`
                pointer-events-none inline-block h-4 w-4 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 
                transition duration-200 ease-in-out
                ${mobileAvailable ? 'translate-x-4 sm:translate-x-5' : 'translate-x-0'}
              `}
            />
          </button>
          <span className="text-xs sm:text-sm text-gray-600 font-medium">
            {mobileLoading ? 'Loading...' : (mobileAvailable ? 'Enabled' : 'Disabled')}
          </span>
          {!editMode && (
            <button
              onClick={() => {
                setFormData(availability);
                setEditMode(true);
              }}
              className="flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8 rounded-lg bg-gray-100/50 hover:bg-gray-200/50 text-gray-600 hover:text-gray-700 transition-all duration-200 ease-out transform hover:scale-105 active:scale-95 touch-manipulation ml-1 sm:ml-2"
            >
              <Pencil className="h-3 w-3 sm:h-4 sm:w-4" />
            </button>
          )}
        </div>
      </div>

      <div className="p-2 sm:p-6">
        <div className="space-y-4">
          {DAYS.map(({ key, label }) => {
            const dayAvailability = (editMode ? formData : availability)?.[key as keyof AssetWorkingAvailability] as AssetDayAvailability;

            const hasHours = dayAvailability.enabled && dayAvailability.shifts && dayAvailability.shifts.length > 0;
            const hasAreas = dayAvailability?.areas && Array.isArray(dayAvailability.areas) && dayAvailability.areas.length > 0;
            const needsAreas = mobileAvailable && hasHours && !hasAreas;

            return (
              <div
                key={key}
                className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                  needsAreas ? 'bg-red-50 border-2 border-red-200' : ''
                }`}
              >
                <div className={`w-24 text-sm capitalize ${needsAreas ? 'text-red-700 font-semibold' : 'text-gray-600'}`}>
                  {label}
                </div>
                {editMode ? (
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={dayAvailability.enabled}
                        onChange={() => handleToggleDay(key as keyof AssetWorkingAvailability)}
                        className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                      />
                      <span className="text-sm text-gray-600">Available</span>
                    </label>
                    {dayAvailability.enabled && (
                      <div className="flex items-center space-x-2">
                        <input
                          type="time"
                          value={dayAvailability.shifts[0]?.start || '09:00'}
                          onChange={(e) => handleShiftChange(key as keyof AssetWorkingAvailability, e.target.value, dayAvailability.shifts[0]?.end || '17:00')}
                          className="w-24 h-9 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                        />
                        <span className="text-sm text-gray-500">to</span>
                        <input
                          type="time"
                          value={dayAvailability.shifts[0]?.end || '17:00'}
                          onChange={(e) => handleShiftChange(key as keyof AssetWorkingAvailability, dayAvailability.shifts[0]?.start || '09:00', e.target.value)}
                          className="w-24 h-9 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-sm flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-2 sm:space-y-0">
                    {dayAvailability.enabled ? (
                      <>
                        <div className={`flex items-center space-x-2 ${needsAreas ? 'text-red-700' : 'text-gray-900'}`}>
                          <span className={needsAreas ? 'text-red-600 font-semibold' : 'text-green-600'}>Available</span>
                          <span className={needsAreas ? 'text-red-600' : 'text-gray-500'}>
                            {dayAvailability.shifts[0]?.start} - {dayAvailability.shifts[0]?.end}
                          </span>
                        </div>
                        {mobileAvailable && (
                          <div className="flex flex-col sm:flex-row sm:items-center space-y-1.5 sm:space-y-0 sm:space-x-2">
                            <button
                              onClick={() => handleAreasClick(label)}
                              className={`px-2 py-1 text-xs font-medium border rounded-md transition-colors duration-200 flex items-center self-start ${
                                needsAreas
                                  ? 'text-red-700 bg-red-50 border-red-300 hover:bg-red-100 hover:border-red-400'
                                  : 'text-blue-600 hover:text-blue-700 border-blue-200 hover:border-blue-300'
                              }`}
                            >
                              <span>Areas</span>
                              {dayAvailability?.areas && dayAvailability.areas.length > 0 && (
                                <span className={`ml-1.5 inline-flex items-center justify-center text-xs font-semibold px-1.5 py-0.5 rounded-full ${
                                  needsAreas
                                    ? 'bg-red-200 text-red-800'
                                    : 'bg-blue-100 text-blue-800'
                                }`}>
                                  {dayAvailability.areas.length}
                                </span>
                              )}
                              {needsAreas && (
                                <span className="ml-1.5 text-red-600 font-semibold">⚠</span>
                              )}
                            </button>
                            {needsAreas && (
                              <span className="text-xs font-medium text-red-600 sm:whitespace-nowrap">
                                No service areas set
                              </span>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      <span className="text-gray-500">Not available</span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {isAreasModalOpen && selectedDay && (
        <ServiceAreasModal
          isOpen={isAreasModalOpen}
          onClose={() => setIsAreasModalOpen(false)}
          businessId={businessId}
          day={selectedDay}
          onSave={handleSaveAreas}
          currentAreas={currentAreas}
        />
      )}
    </div>
  );
};

const AssetAvailability: React.FC<AssetAvailabilityProps> = (props) => {
  return <AssetAvailabilityComponent key={props.assetId} {...props} />;
};

export default AssetAvailability;
